import { Trans } from "react-i18next"
import { WarningCircle } from "@phosphor-icons/react"
import { SPECIALTY_SELECT_INPUT_ID } from "@/modules/patients/detail/progress-note/components/specialty-select"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import type { BetterDocConsultationDetail } from "@/types/__generated__/union-types"

type BetterDocCareProviderSelectionBlockedHintProps = {
  missingConsultationDetailsThatCanChangeBetterDocUsage: ReadonlyArray<BetterDocConsultationDetail>
  progressNoteId: string | undefined
}

const HINT_LINK_CLASS_NAME = "cursor-pointer font-medium underline underline-offset-2"

const getHintTranslationKey = (missingConsultationDetails: ReadonlyArray<BetterDocConsultationDetail>) => {
  const isDiagnosisCodeMissing = missingConsultationDetails.includes("DIAGNOSIS_CODE")
  const isSpecialtyMissing = missingConsultationDetails.includes("SPECIALTY")

  if (isDiagnosisCodeMissing && isSpecialtyMissing) {
    return "episodeOfCare.progressNote.pointOfCare.betterDocCareProviderSelectionBlocked.diagnosisCodeAndSpecialty"
  }

  return isSpecialtyMissing
    ? "episodeOfCare.progressNote.pointOfCare.betterDocCareProviderSelectionBlocked.specialty"
    : "episodeOfCare.progressNote.pointOfCare.betterDocCareProviderSelectionBlocked.diagnosisCode"
}

export const BetterDocCareProviderSelectionBlockedHint = ({
  missingConsultationDetailsThatCanChangeBetterDocUsage,
  progressNoteId,
}: BetterDocCareProviderSelectionBlockedHintProps) => {
  const setProgressNoteIdWithOpenEpisodeOfCareDropdown = useEpisodeOfCareStoreContext(
    (state) => state.setProgressNoteIdWithOpenEpisodeOfCareDropdown,
  )

  const openEpisodeOfCareDropdown = () => {
    if (progressNoteId) {
      setProgressNoteIdWithOpenEpisodeOfCareDropdown(progressNoteId)
    }
  }

  const openSpecialtySelect = () => {
    if (!progressNoteId) return
    const specialtyInput = document
      .getElementById(progressNoteId)
      ?.querySelector<HTMLInputElement>(`#${SPECIALTY_SELECT_INPUT_ID}`)
    specialtyInput?.focus()
    specialtyInput?.click()
  }

  return (
    <p
      className="text-critical relative mt-0.75 ml-4 pl-5 text-start text-xs tracking-wide"
      data-testid="better-doc-care-provider-selection-blocked-hint">
      <span className="absolute top-px left-0 leading-none">
        <WarningCircle className="h-4 w-4" />
      </span>
      <Trans
        i18nKey={getHintTranslationKey(missingConsultationDetailsThatCanChangeBetterDocUsage)}
        components={{
          episodeOfCareLink: (
            <button
              type="button"
              className={HINT_LINK_CLASS_NAME}
              data-testid="better-doc-care-provider-selection-blocked-episode-of-care-link"
              onClick={openEpisodeOfCareDropdown}
            />
          ),
          specialtyLink: (
            <button
              type="button"
              className={HINT_LINK_CLASS_NAME}
              data-testid="better-doc-care-provider-selection-blocked-specialty-link"
              onClick={openSpecialtySelect}
            />
          ),
        }}
      />
    </p>
  )
}
